import { TimesheetRow, TimesheetRowDimension } from '@sit/api-types';
import { GetApiDimensionsResponse, RequestError } from '@sit/client-shared';
import { InvalidateQueryFilters, Query, QueryClient, QueryKey, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useGetDimensions } from '@web/api/dimensions/use-get-dimensions';
import { addRequest, removeRequest } from '@web/redux/TimesheetStatus';
import { useShowDepartmentLocation } from '@web/redux/hooks';
import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loadAutofilledDimensionValues, loadAutofilledDimensionValuesToRemove } from '../../containers/Timesheet/helpers';
import api from '../../helpers/api';
import { updateSyncStatus } from '../../redux/Timesheet/actions';
import { selectRowAutofill, selectToken } from '../../redux/selectors';
import { useAppSelector } from '../../redux/store';
import { AutofillStoreRow } from '../../types/redux/stores/autofill.types';
import { queryClient } from './query/client';

interface UseTimesheetQueriesParams {
  timesheetId: string;
  id: string;
}

// QUERY TIMESHEET ROWS

export const TIMESHEET_ROWS_QUERY_KEY = 'QueryTimesheetRows';

interface UseQueryTimesheetRowsParams {
  timesheetId: string;
}

interface GetTimesheetRowsParams {
  token: string | null;
  params: UseQueryTimesheetRowsParams;
}

export const invalidateTimesheetRow = (timesheetId: string, options: InvalidateQueryFilters = {}) => ({
  predicate: (query: Query<unknown, unknown, unknown, QueryKey>) =>
    query.queryKey[0] === TIMESHEET_ROWS_QUERY_KEY && query.queryKey[1] === timesheetId,
  ...options,
});

const getTimesheetRows = async ({ token, params: { timesheetId, ...params } }: GetTimesheetRowsParams) => {
  return await api.send<TimesheetRow[]>({
    url: `/timesheets/${timesheetId}/rows`,
    method: 'GET',
    cache: 'default',
    token,
    params,
    skipToast: true,
  });
};

export const useTimesheetRows = (params: UseQueryTimesheetRowsParams) => {
  const token = useSelector(selectToken);

  return useQuery<TimesheetRow[], RequestError>([TIMESHEET_ROWS_QUERY_KEY, params.timesheetId], () => getTimesheetRows({ token, params }), {
    enabled: !!params.timesheetId,
  });
};

export const fetchQueryTimesheetRows = (params: UseQueryTimesheetRowsParams, token: string) =>
  queryClient.fetchQuery([TIMESHEET_ROWS_QUERY_KEY, params.timesheetId], () => getTimesheetRows({ token, params }));

// MUTATE TIMESHEET ROW
export const TIMESHEET_ROWS_MUTATION_KEY = 'MutationTimesheetRows';

interface PutTimesheetRowsParams {
  token: string | null;
  params: UseTimesheetQueriesParams;
  data: {
    dimensionValues: TimesheetRowDimension;
  };
}
const putTimesheetRow = async ({ token, params: { timesheetId, id }, data }: PutTimesheetRowsParams, queryClient: QueryClient) => {
  const response = await api.send({
    url: `/timesheets/${timesheetId}/rows/${id}`,
    method: 'PUT',
    data,
    token,
  });

  queryClient.invalidateQueries(invalidateTimesheetRow(timesheetId));

  return response;
};

export const useMutateTimesheetRow = (params: UseTimesheetQueriesParams) => {
  const token = useSelector(selectToken);
  const dispatch = useDispatch();

  const queryClient = useQueryClient();

  const { mutateAsync, ...data } = useMutation(
    [TIMESHEET_ROWS_MUTATION_KEY, params.timesheetId, params.id],
    (data: { dimensionValues: TimesheetRowDimension }) => putTimesheetRow({ token, params, data }, queryClient),
  );

  const updateTimesheet = async (payload: { dimensionValues: TimesheetRowDimension }) => {
    dispatch(addRequest({ name: 'TIMESHEET_ROW_UPDATE' }));
    await mutateAsync(payload);
    dispatch(updateSyncStatus(false));
    dispatch(removeRequest({ name: 'TIMESHEET_ROW_UPDATE' }));
  };

  return { updateTimesheet, ...data };
};

// MUTATE TIMESHEET BILLABLE DATA

interface PutTimesheetRowBillable {
  token: string | null;
  params: UseTimesheetQueriesParams;
  data: boolean;
}

const putTimesheetRowBillable = async ({ token, params: { timesheetId, id }, data: billable }: PutTimesheetRowBillable) => {
  const response = await api.send({
    url: `/timesheets/${timesheetId}/rows/${id}/billable`,
    method: 'PUT',
    data: { billable },
    token,
  });

  return response;
};

export const useMutateBillable = (params: UseTimesheetQueriesParams) => {
  const token = useSelector(selectToken);
  const dispatch = useDispatch();

  const { mutateAsync, ...data } = useMutation((data: boolean) => putTimesheetRowBillable({ token, params, data }));

  const updateBillable = async (billable: boolean) => {
    dispatch(addRequest({ name: 'TIMESHEET_ROW_BILLABLE_UPDATE' }));
    await mutateAsync(billable);
    dispatch(updateSyncStatus(false));
    dispatch(removeRequest({ name: 'TIMESHEET_ROW_BILLABLE_UPDATE' }));
  };

  return { updateBillable, ...data };
};

// MUTATE TIMESHEET ROW DIMENSIONS

export interface TimesheetDimensions {
  client: string | null;
  item: string | null;
  project: string | null;
  task: string | null;
  timeType: string | null;
  department: string | null;
  location: string | null;
  costType: string | null;
  employeePosition: string | null;
  laborClass: string | null;
  laborShift: string | null;
  laborUnion: string | null;
}
interface PutTimesheetRowDimensions {
  token: string | null;
  params: UseTimesheetQueriesParams;
  data: TimesheetDimensions;
}

interface DimensionCache {
  previousOtherValues: string[];
  dimensions: GetApiDimensionsResponse | undefined;
  autofills: AutofillStoreRow | null;
  sendDepartmentData: boolean;
}

const putTimesheetRowDimensions = async (
  { token, params: { timesheetId, id }, data: rawData }: PutTimesheetRowDimensions,
  { previousOtherValues, dimensions, autofills, sendDepartmentData }: DimensionCache,
) => {
  const { department, location, costType, employeePosition, laborClass, laborShift, laborUnion, ...data } = rawData;
  const otherValues = loadAutofilledDimensionValues({
    department: sendDepartmentData ? department : null,
    location: sendDepartmentData ? location : null,
    costType,
    employeePosition,
    laborClass,
    laborShift,
    laborUnion,
    dimensions,
    rowAutofill: autofills,
  });

  const newData = {
    ...data,
    otherValues,
    removeValues: loadAutofilledDimensionValuesToRemove({
      otherValues,
      prevOtherValues: previousOtherValues,
    }),
  };

  const response = await api.send({
    url: `/timesheets/${timesheetId}/rows/${id}/dimension-values`,
    method: 'PUT',
    data: newData,
    token,
  });

  return { response, otherValues };
};

export const usePutTimesheetDimensions = (params: UseTimesheetQueriesParams) => {
  const token = useAppSelector(selectToken);
  const { data: dimensions } = useGetDimensions({
    timesheetId: params.timesheetId,
  });
  const autofills = useAppSelector((state) => selectRowAutofill(state, params.id));
  const sendDepartmentData = useShowDepartmentLocation();
  const dispatch = useDispatch();

  const previousOtherValues = useRef<string[]>([]);

  const { data, mutateAsync, ...rest } = useMutation((data: TimesheetDimensions) =>
    putTimesheetRowDimensions(
      { token, params, data },
      {
        previousOtherValues: previousOtherValues.current,
        dimensions,
        autofills,
        sendDepartmentData,
      },
    ),
  );

  const updateDimensions = async (payload: TimesheetDimensions) => {
    dispatch(addRequest({ name: 'TIMESHEET_ROW_DIMENSIONS_UPDATE' }));
    await mutateAsync(payload);
    dispatch(updateSyncStatus(false));
    dispatch(removeRequest({ name: 'TIMESHEET_ROW_DIMENSIONS_UPDATE' }));
  };

  if (data) {
    previousOtherValues.current = data.otherValues;

    return {
      updateDimensions,
      data: data.response,
      ...rest,
    };
  }

  return { updateDimensions, data, ...rest };
};
